/**
 * Pure helpers over a wiki entry's web / desktop bindings.
 *
 * Which product serves an entry is decided from the API payload alone: a
 * child without web bindings of its own inherits its parent's (a component of
 * "Kataster nepremičnin" toggles with the parent's control), an entry with
 * its own bindings keeps them.
 */

import type { WikiEntry, WikiWeb } from '../../shared/types/layerWiki.js';

/** True when the entry declares any toggleable web binding of its own. */
export function hasOwnWebBindings(entry: WikiEntry): boolean {
  const w = entry.web;
  return (
    w.controls.length > 0 ||
    w.gursDefaults.length > 0 ||
    w.czkDefaults.length > 0 ||
    w.sources.length > 0 ||
    w.presets.length > 0
  );
}

export type WikiSurface = 'web' | 'desktop' | 'both' | 'none';

/** KingCAD serves the entry itself: a layer on a desktop map, or a DOF / LiDAR product read through the catalog routes. */
export function hasOwnDesktop(entry: WikiEntry): boolean {
  return (
    entry.desktop.maps.length > 0 ||
    entry.desktop.layerCount > 0 ||
    entry.desktop.catalogs.length > 0
  );
}

/**
 * A PART of a product: a `parentKey` child that is neither a preset (it restyles its
 * base layer on the web only) nor a computed layer (bindings of its own). A part is
 * served wherever its product is and, without a ledger of its own, dated by it.
 */
export function isPartOf(entry: WikiEntry): boolean {
  return entry.parentKey !== null && entry.web.presets.length === 0 && entry.cadence !== 'computed';
}

/**
 * Which product serves the entry: `web` when it (or its parent) has a web toggle,
 * `desktop` when KingCAD maps list it, `both` when both. The page shows a chip
 * only for the single-surface cases — "both" is the default and needs no label.
 *
 * A PART of a product (a `parentKey` child that is neither a preset nor a computed
 * layer) is served wherever the product is: without desktop bindings of its own it
 * inherits its parent's desktop surface (2026-09-28 — the five DRAJV layers each
 * carry their own web toggle while the umbrella owns the table the KingCAD layers
 * read; »Samo splet« on them would be false). A preset restyles its base layer on
 * the web only; a computed layer has bindings of its own.
 */
export function surfaceOf(entry: WikiEntry, parent: WikiEntry | null | undefined): WikiSurface {
  const web = effectiveWeb(entry, parent);
  // A toggle of its own, or tiles that actually serve (an archive-backed umbrella such as the
  // ČÚZK "Katastrální mapa" has no toggle — its children do — but its archive IS the web map).
  const hasWeb =
    web.controls.length > 0 ||
    web.gursDefaults.length > 0 ||
    web.czkDefaults.length > 0 ||
    web.sources.length > 0 ||
    web.presets.length > 0 ||
    isServedOnWeb(web);
  const hasDesktop = hasOwnDesktop(entry) || (isPartOf(entry) && !!parent && hasOwnDesktop(parent));
  if (hasWeb && hasDesktop) return 'both';
  if (hasWeb) return 'web';
  if (hasDesktop) return 'desktop';
  return 'none';
}

/** Tiles for this entry are served on the web right now (built archive or a live route). */
export function isServedOnWeb(web: WikiWeb): boolean {
  return web.state === 'current' || web.state === 'behind' || web.state === 'live';
}

/** The entry's web bindings, or its parent's when it has none of its own. */
export function effectiveWeb(entry: WikiEntry, parent: WikiEntry | null | undefined): WikiWeb {
  if (hasOwnWebBindings(entry)) return entry.web;
  if (parent && hasOwnWebBindings(parent)) return parent.web;
  return entry.web;
}
